import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type {
  InsufficientAssessmentError,
  RiskResult,
  WorkEnvironmentRisk,
} from '../../../api/workEnvironmentTypes';
import {
  useArchiveInsufficientSurveyMutation,
  useExtendDeadlineMutation,
  useSaveInsufficientAssessmentMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import {
  deadlineBounds,
  endOfDay,
  FieldError,
  INSUFFICIENT_FIELDS,
  decidesAlone,
  insufficientFieldErrors,
  insufficientTextSuffix,
  insufficientForm,
  InsufficientForm,
  validationErrors,
} from '../assessmentView';
import { areaName, dayAndTime } from '../riskView';
import { FormErrorSummary, SavedText, TextField } from './FormParts';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';
import { TakenOverNotice } from './TakenOverNotice';

type Insufficient = NonNullable<Extract<WorkEnvironmentRisk, { calculatedAt: string }>['insufficient']>;

type Props = {
  surveyId: number;
  risk: Extract<RiskResult, { status: 'insufficientResponses' }>;
  insufficient: Insufficient;
};

/**
 * Too few answered to show numbers. The team can get more time, or the safety representative
 * or the committee says how they read it and what they do instead before the survey is archived.
 */
export const InsufficientCard = ({ surveyId, risk, insufficient }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { saved, canDecide, attempts, assessors } = insufficient;
  const { may } = useSurveyActions();
  // The team leader registers the assessment and decides what happens next
  const assesses = canDecide && may('assess');
  const advances = canDecide && may('advance');
  const suffix = insufficientTextSuffix(assessors, insufficient.safetyRepMissing);
  const bounds = deadlineBounds(new Date(), insufficient.maxExtensionDays);
  const [deadline, setDeadline] = useState(bounds.min);
  const [editing, setEditing] = useState(!saved && assesses);
  const [form, setForm] = useState<InsufficientForm>(() => insufficientForm(saved));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const extend = useExtendDeadlineMutation(surveyId);
  const save = useSaveInsufficientAssessmentMutation(surveyId);
  const archive = useArchiveInsufficientSurveyMutation(surveyId);
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form, {
      onSuccess: () => {
        setErrors([]);
        setEditing(false);
      },
      onError: (error) => {
        const invalid = validationErrors<InsufficientAssessmentError>(error);
        if (invalid) setErrors(insufficientFieldErrors(invalid, t));
      },
    });
  };

  return (
    <section aria-labelledby="insufficient-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="insufficient-heading">
            {t(canDecide ? 'workEnvironmentRisk.insufficient.title' : 'workEnvironmentRisk.insufficient.archivedTitle')}
          </Heading>
          <Paragraph>
            {t('workEnvironmentRisk.insufficient.text', { responses: risk.responses, min: risk.minResponses })}
          </Paragraph>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.insufficient.attempts', { count: attempts })}
          </Paragraph>
        </div>
        {risk.requiresFollowUp.length > 0 && (
          <div className={classes.areaBody}>
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentRisk.insufficient.followUp')}
            </Heading>
            <Paragraph data-size="sm">{t(`workEnvironmentRisk.insufficient.followUpText${suffix}`)}</Paragraph>
            <ul className={classes.followUpList}>
              {risk.requiresFollowUp.map((area) => (
                <li key={area.key} className="ds-paragraph" data-size="sm">
                  {areaName(area, t)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {advances && (
        <form
          className={process.cardFooter}
          onSubmit={(event) => {
            event.preventDefault();
            extend.mutate(endOfDay(deadline));
          }}
        >
          <div className={classes.areaBody}>
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentRisk.insufficient.extendTitle')}
            </Heading>
            <Paragraph data-size="sm">{t('workEnvironmentRisk.insufficient.extendText')}</Paragraph>
            <div className={classes.deadlineRow}>
              <Field data-size="sm">
                <Label htmlFor="insufficient-deadline">{t('workEnvironmentRisk.insufficient.deadline')}</Label>
                <Input
                  id="insufficient-deadline"
                  type="date"
                  min={bounds.min}
                  max={bounds.max}
                  value={deadline}
                  onChange={(event) => setDeadline(event.target.value)}
                />
              </Field>
              <Button type="submit" variant="primary" data-size="sm" loading={extend.isPending}>
                {t('workEnvironmentRisk.insufficient.extend')}
              </Button>
            </div>
            {extend.isError && (
              <Alert data-color="danger">
                <Paragraph>{t('workEnvironmentRisk.insufficient.extendFailed')}</Paragraph>
              </Alert>
            )}
          </div>
        </form>
      )}

      <div className={process.cardFooter}>
        <div className={classes.areaBody}>
          <Heading level={3} data-size="2xs">
            {t('workEnvironmentRisk.insufficient.assessmentTitle')}
          </Heading>
          {assesses && editing ? (
            <form onSubmit={submit} noValidate className={classes.areaBody}>
              <Paragraph data-size="sm">{t(`workEnvironmentRisk.insufficient.assessmentText${suffix}`)}</Paragraph>
              <TakenOverNotice emails={insufficient.takenOverBy} />
              <FormErrorSummary errors={errors} />
              {!decidesAlone(assessors) && (
                <Fieldset data-size="sm">
                  <Fieldset.Legend>{t('workEnvironmentRisk.insufficient.assessedBy')}</Fieldset.Legend>
                  {assessors.map((assessor) => (
                    <Radio
                      key={assessor}
                      id={`insufficient-assessed-by-${assessor}`}
                      name="insufficient-assessed-by"
                      value={assessor}
                      label={t(`workEnvironmentRisk.insufficient.assessors.${assessor}`)}
                      checked={form.assessedBy === assessor}
                      onChange={() => setForm({ ...form, assessedBy: assessor })}
                    />
                  ))}
                  {errorFor(INSUFFICIENT_FIELDS.assessedBy) && (
                    <ValidationMessage>{errorFor(INSUFFICIENT_FIELDS.assessedBy)}</ValidationMessage>
                  )}
                </Fieldset>
              )}
              <TextField
                id={INSUFFICIENT_FIELDS.interpretation}
                label={t('workEnvironmentRisk.insufficient.interpretation')}
                value={form.interpretation}
                error={errorFor(INSUFFICIENT_FIELDS.interpretation)}
                onChange={(interpretation) => setForm({ ...form, interpretation })}
              />
              <TextField
                id={INSUFFICIENT_FIELDS.alternative}
                label={t('workEnvironmentRisk.insufficient.alternative')}
                help={t('workEnvironmentRisk.insufficient.alternativeHelp')}
                value={form.alternative}
                error={errorFor(INSUFFICIENT_FIELDS.alternative)}
                onChange={(alternative) => setForm({ ...form, alternative })}
              />
              {save.isError && !validationErrors(save.error) && (
                <Alert data-color="danger">
                  <Paragraph>{t('workEnvironmentRisk.assessment.saveFailed')}</Paragraph>
                </Alert>
              )}
              <div className={process.linkRow}>
                <Button type="submit" variant="secondary" data-size="sm" loading={save.isPending}>
                  {t('workEnvironmentRisk.assessment.save')}
                </Button>
                {saved && (
                  <Button type="button" variant="tertiary" data-size="sm" onClick={() => setEditing(false)}>
                    {t('workEnvironmentRisk.assessment.cancel')}
                  </Button>
                )}
              </div>
            </form>
          ) : !saved && canDecide ? (
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentRisk.assessment.notSavedYet')}
            </Paragraph>
          ) : (
            saved && (
              <>
                <SavedText
                  title={t('workEnvironmentRisk.insufficient.assessedBy')}
                  text={t(`workEnvironmentRisk.insufficient.assessors.${saved.assessedBy}`)}
                />
                <SavedText title={t('workEnvironmentRisk.insufficient.interpretation')} text={saved.interpretation} />
                <SavedText title={t('workEnvironmentRisk.insufficient.alternative')} text={saved.alternative} />
                <Paragraph data-size="xs" className={process.subtle}>
                  {t(
                    saved.savedBy ? 'workEnvironmentRisk.assessment.savedBy' : 'workEnvironmentRisk.assessment.savedAt',
                    { email: saved.savedBy, when: dayAndTime(saved.savedAt, t, i18n.language) },
                  )}
                </Paragraph>
                {(advances || assesses) && (
                  <>
                    {advances && <Paragraph data-size="sm">{t('workEnvironmentRisk.insufficient.archiveHelp')}</Paragraph>}
                    {archive.isError && (
                      <Alert data-color="danger">
                        <Paragraph>{t('workEnvironmentRisk.insufficient.archiveFailed')}</Paragraph>
                      </Alert>
                    )}
                    <div className={process.linkRow}>
                      {advances && (
                        <Button
                          variant="secondary"
                          data-size="sm"
                          loading={archive.isPending}
                          onClick={() => archive.mutate(undefined)}
                        >
                          {t('workEnvironmentRisk.insufficient.archive')}
                        </Button>
                      )}
                      {assesses && (
                        <Button variant="tertiary" data-size="sm" onClick={() => setEditing(true)}>
                          {t('workEnvironmentRisk.assessment.edit')}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            )
          )}
        </div>
      </div>
    </section>
  );
};
